import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { NavigationService } from 'app/core/services/navigation.service';
import { buildBreadcrumbs } from 'app/utils/breadcrumb.utils';
import { IBreadCrumb } from '../breadcrumb/breadcrumb.interface';

/** Breadcrumb label of the home page, the only page without a back button. */
const DASHBOARD_BREADCRUMB = 'breadcrumb.title-dashboard';

/**
 * Title of the current page, at the top of the content of every logged-in page.
 *
 * @remarks
 * Built from the `data.breadcrumb` of the active routes, so no page declares
 * its own title: the last crumb is the heading and the others form the trail
 * above it. Below 1024px a back button sits before the title on every page but
 * the dashboard, where the header used to show it.
 */
@Component({
  standalone: true,
  selector: 'app-page-title',
  imports: [TranslateModule],
  templateUrl: './page-title.component.html',
  styleUrls: ['./page-title.component.scss'],
})
export class PageTitleComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly navigationService = inject(NavigationService);
  private readonly destroy$ = new Subject<void>();

  parents: IBreadCrumb[] = [];
  current: IBreadCrumb | null = null;
  showBackButton = false;

  ngOnInit(): void {
    this.refresh();
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.refresh());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  goBack(): void {
    this.navigationService.goBack();
  }

  private refresh(): void {
    const crumbs = buildBreadcrumbs(this.activatedRoute.root);
    this.parents = crumbs.slice(0, -1);
    this.current = crumbs[crumbs.length - 1] ?? null;
    this.showBackButton =
      crumbs.length > 0 && crumbs[0].label !== DASHBOARD_BREADCRUMB;
  }
}
